import React from "react";
import { AlertTriangle, ChevronLeft, ChevronRight, CloudAlert, CloudCheck, LoaderCircle, Pencil } from "lucide-react";
import DrawingAnswerSection from "./DrawingAnswerSection";
import QuestionImage from "./QuestionImage";
import StudentMcqResponse from "./StudentMcqResponse";

const SAVE_LABELS = {
  saved: "All changes saved",
  pending: "Saving…",
  saving: "Saving…",
  failed: "Not saved",
  conflict: "Needs your attention",
};

// Reflects acknowledged server state only: "All changes saved" appears once
// every answer on the page has been confirmed by the server.
export const SaveStatus = ({ state, onRetry }) => {
  const Icon = state === "saved" ? CloudCheck : state === "pending" || state === "saving" ? LoaderCircle : CloudAlert;
  return (
    <div className={`eq-assess-save-status is-${state}`} role="status" aria-live="polite">
      <Icon size={20} aria-hidden="true" className={state === "saving" || state === "pending" ? "eq-assess-spin" : undefined} />
      <span>{SAVE_LABELS[state]}</span>
      {state === "failed" && onRetry && (
        <button type="button" className="eq-assess-link-button" onClick={onRetry}>Retry</button>
      )}
    </div>
  );
};

const marksLabel = (maxMark) =>
  Number.isFinite(maxMark) && maxMark > 0 ? `${maxMark} mark${maxMark === 1 ? "" : "s"}` : null;

const conflictDisplay = (question, text) => {
  if (question.questionType === "multipleChoice") {
    return question.options.find((option) => option.id === text)?.text || "No option selected";
  }
  return text.trim() ? text : "(blank)";
};

// One question at a time. All answer state lives in the page; this component
// only renders it and reports edits, so changing question never loses work.
// `drawing` (open-ended questions only, when drawing is available or a
// drawing already exists) adds Draw and the saved-drawing preview.
const AssessmentQuestion = ({
  question,
  index,
  total,
  headingRef,
  value,
  locked,
  overallSaveState,
  manualSaving,
  conflictText,
  allowPaste,
  pasteBlocked,
  quizId,
  mediaContext,
  onChange,
  onPasteBlocked,
  onSave,
  onEdit,
  onRetrySave,
  onResolveConflict,
  onPrevious,
  onNext,
  attemptId,
  drawing = null,
}) => {
  const isMultipleChoice = question.questionType === "multipleChoice";
  const headingId = `eq-question-heading-${question.id}`;
  const answerId = `eq-answer-${question.id}`;
  const marks = marksLabel(question.maxMark);
  const hasConflict = conflictText !== undefined;
  const isLast = index === total - 1;
  const canDraw = !isMultipleChoice && Boolean(drawing);

  return (
    <article className={`eq-question-card eq-assess-question${locked ? " saved" : ""}`} aria-labelledby={headingId}>
      <div className="eq-assess-question-top">
        <h2 id={headingId} className="eq-assess-kicker" tabIndex={-1} ref={headingRef}>
          Question {index + 1} of {total}
        </h2>
        {marks && <span className="eq-assess-marks">{marks}</span>}
      </div>

      {(question.questionImageURL || question.questionMedia) && (
        <div className="eq-question-image eq-assess-question-image">
          <QuestionImage question={question} quizId={quizId} context={mediaContext} />
        </div>
      )}
      {question.questionText && (
        <p className="eq-question-text eq-assess-question-text">{question.questionText}</p>
      )}

      <div className="eq-assess-answer">
        {isMultipleChoice ? (
          <>
            <h3 className="eq-assess-answer-label">Your answer</h3>
            <StudentMcqResponse
              question={question}
              value={value}
              disabled={locked}
              onChange={onChange}
            />
          </>
        ) : (
          <>
            <label className="eq-assess-answer-label" htmlFor={answerId}>Your answer</label>
            <textarea
              id={answerId}
              rows="6"
              value={value}
              onChange={(event) => onChange(event.target.value)}
              className={`eq-textarea eq-assess-textarea${locked ? " saved" : ""}`}
              placeholder={canDraw ? "Type an explanation, or add a drawing below..." : "Type your answer here..."}
              readOnly={locked}
              disabled={locked}
              onPaste={(event) => {
                if (!allowPaste) {
                  event.preventDefault();
                  onPasteBlocked();
                }
              }}
            />
          </>
        )}

        {pasteBlocked && (
          <p className="eq-paste-warning" role="alert">⛔ Pasting is disabled for this test.</p>
        )}

        {hasConflict && (
          <div className="eq-assess-conflict" role="alert">
            <AlertTriangle size={20} aria-hidden="true" />
            <div>
              <strong>This answer was changed in another window.</strong>
              <p>The saved version is:</p>
              <blockquote>{conflictDisplay(question, conflictText)}</blockquote>
              <div className="eq-assess-conflict-actions">
                <button type="button" className="eq-assess-secondary" onClick={() => onResolveConflict("mine")}>
                  Keep my answer
                </button>
                <button type="button" className="eq-assess-secondary" onClick={() => onResolveConflict("saved")}>
                  Use the saved version
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="eq-btn-row eq-assess-actions">
          <div className="eq-assess-action-buttons">
            <button
              type="button"
              onClick={onSave}
              className={`eq-save-btn eq-assess-primary${locked ? " saved" : ""}`}
              disabled={locked || manualSaving || hasConflict}
              aria-busy={manualSaving}
            >
              {locked ? "✓ Saved" : manualSaving ? "Saving…" : "Save answer"}
            </button>
            {canDraw && (
              <button
                type="button"
                className="eq-assess-secondary eq-assess-draw"
                onClick={drawing.onOpen}
                disabled={locked || Boolean(drawing.busy) || drawing.padOpen}
              >
                <Pencil size={18} aria-hidden="true" />Draw
              </button>
            )}
            {locked && (
              <button type="button" className="eq-edit-btn eq-assess-secondary" onClick={onEdit}>
                Edit Answer
              </button>
            )}
          </div>
          <SaveStatus state={overallSaveState} onRetry={onRetrySave} />
        </div>

        {canDraw && (
          <DrawingAnswerSection attemptId={attemptId} questionId={question.id} drawing={drawing} locked={locked} />
        )}
      </div>

      <footer className="eq-assess-question-nav">
        <button type="button" className="eq-assess-secondary eq-assess-prev" onClick={onPrevious} disabled={index === 0}>
          <ChevronLeft size={19} aria-hidden="true" />Previous
        </button>
        <span className="eq-assess-position">{index + 1} of {total}</span>
        <button type="button" className="eq-assess-next" onClick={onNext}>
          {isLast ? "Review answers" : "Next question"}<ChevronRight size={19} aria-hidden="true" />
        </button>
      </footer>
    </article>
  );
};

export default AssessmentQuestion;
